// src/pages/Accounting/Reports/AccountList/components/ReportBody.tsx
// Printable body of the Account List report.
// Wrapped in .print-area so Print and Export only capture this block.
// Centered company header + report title + Generated on + table.

import React from 'react';
import { fmtCurrency, fmtGeneratedOn, visibleColumns, applyCustomization } from '../utils';
import { DEFAULT_COLUMNS } from '../constants';
import type { AccountListReportData, ReportCustomization } from '../types';

interface ReportBodyProps {
  data: AccountListReportData;
  customization: ReportCustomization;
  density: 'compact' | 'standard' | 'spacious';
  zoom: number;
}

export default function ReportBody({ data, customization, density, zoom }: ReportBodyProps) {
  const rows = applyCustomization(data.rows, customization);
  const activeColumns = visibleColumns(customization);
  const colDefMap = Object.fromEntries(DEFAULT_COLUMNS.map(function (c) { return [c.key, c]; }));

  const padY = density === 'compact' ? 'py-2' : density === 'spacious' ? 'py-3.5' : 'py-2.5';
  const fontSize = density === 'compact' ? 'text-[12.5px]' : 'text-[13px]';
  const align = customization.header.alignment;
  const alignCls = align === 'left' ? 'text-left' : align === 'right' ? 'text-right' : 'text-center';

  return (
    <div
      className="print-area"
      style={{
        zoom: zoom / 100,
      }}
    >
      <div className={`${alignCls} mb-6`}>
        {customization.header.showCompanyName && (
          <h3 className="text-[22px] font-extrabold text-ink m-0 mb-1">{data.companyName}</h3>
        )}
        {customization.header.showReportTitle && (
          <div className="text-[15px] text-ink2 mb-1">{data.reportTitle}</div>
        )}
        {customization.header.showReportPeriod && (
          <div className="text-[11.5px] text-ink3">{fmtGeneratedOn(data.generatedAt)}</div>
        )}
      </div>

      <table className="w-full border-collapse">
        <thead>
          <tr>
            {activeColumns.map(function (c) {
              const def = colDefMap[c.key as string];
              const alignCol = def?.align === 'right' ? 'text-right' : 'text-left';
              const colorStyle = customization.columnColors[c.key as string]
                ? { backgroundColor: customization.columnColors[c.key as string] }
                : undefined;
              return (
                <th
                  key={c.key as string}
                  className={`text-[11.5px] font-bold text-ink ${alignCol} px-3.5 py-2.5 border-t-2 border-t-ink border-b border-b-line bg-white`}
                  style={colorStyle}
                >
                  {def?.label ?? c.key}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map(function (row, i) {
            const bandedStyle =
              customization.rowSettings.bandedRowColor && i % 2 === 1
                ? { backgroundColor: customization.rowSettings.bandedRowColor }
                : undefined;
            return (
              <tr key={row.id} style={bandedStyle}>
                {activeColumns.map(function (c) {
                  const def = colDefMap[c.key as string];
                  const alignCol = def?.align === 'right' ? 'text-right' : 'text-left';
                  const raw = (row as unknown as Record<string, unknown>)[c.key as string];
                  let display: string;
                  if (c.key === 'totalBalance') {
                    display = fmtCurrency(raw as number | null, {
                      hideSymbol: customization.numberFormat.hideCurrencySymbol,
                      divideByThousand: customization.numberFormat.divideByThousand,
                      negativeStyle: customization.numberFormat.negativeStyle,
                    });
                  } else if (raw == null || raw === '') {
                    display = customization.cell.emptyCellStyle === 'dash' ? '-' : '';
                  } else {
                    display = String(raw);
                  }
                  return (
                    <td
                      key={c.key as string}
                      className={`${padY} px-3.5 ${fontSize} text-ink2 border-b border-b-lineSoft ${alignCol}`}
                    >
                      {display}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}